import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container, Grid, Icon } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import SpaIcon from "@mui/icons-material/Spa";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import { StarOutline } from "@mui/icons-material";

const cardConfigs = [
  {
    iconBg: "from-[#7393B3] to-[#5A7A9A]",
    iconColor: "#fff",
    Icon: BoltIcon,
    cardBg: "bg-gradient-to-br from-[#0A0F2E] to-[#131836]",
    textColor: "text-white",
    descColor: "text-slate-400",
    accentColor: "#7393B3",
  },
  {
    iconBg: "from-[#8B5CF6] to-[#7C3AED]",
    iconColor: "#fff",
    Icon: SpaIcon,
    cardBg: "",
    textColor: "text-[#2D1B4E]",
    descColor: "text-[#6B5B7B]",
    accentColor: "#8B5CF6",
    isGlass: true,
  },
  {
    iconBg: "from-[#C084FC] to-[#A855F7]",
    iconColor: "#fff",
    Icon: NightsStayIcon,
    cardBg: "",
    textColor: "text-[#2D1B4E]",
    descColor: "text-[#6B5B7B]",
    accentColor: "#C084FC",
    isGlass: true,
  },
];

const BenefitsSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const title = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.benefits.title",
  });
  const subtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.benefits.subtitle",
  });

  const benefits = [0, 1, 2].map((i) => ({
    title: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.benefits.items.${i}.title`,
    }),
    desc: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.benefits.items.${i}.description`,
    }),
    ...cardConfigs[i],
  }));

  return (
    <Box className="py-28 relative" id="features" sx={{ background: "transparent" }}>
      {/* Luminous dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.2] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(139,92,246,0.3) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Atmospheric blobs */}
      <motion.div
        className="absolute top-[10%] right-[-8%] w-[35%] h-[40%] rounded-full bg-[#E0CDF5]/40 blur-[120px] pointer-events-none"
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[5%] left-[-5%] w-[30%] h-[35%] rounded-full bg-[#F5E1EC]/35 blur-[100px] pointer-events-none"
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 4 }}
      />

      <Container maxWidth="xl" className="relative z-10" dir={dir}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 px-4"
        >
          <Typography
            variant="overline"
            className="font-bold tracking-[0.2em] mb-3 block"
            sx={{
              background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            BENEFITS
          </Typography>
          <Typography
            variant="h2"
            className="text-4xl md:text-5xl font-bold tracking-tight text-[#2D1B4E] mb-4"
          >
            {title}
          </Typography>
          <Typography className="text-lg text-[#6B5B7B] max-w-2xl mx-auto">
            {subtitle}
          </Typography>
        </motion.div>

        {/* Bento-style grid */}
        <div className="px-4 md:px-8">
          <Grid container spacing={4}>
            {/* First card – wide dark card */}
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
                className="h-full"
              >
                <div
                  className={`h-full p-8 md:p-12 rounded-[2rem] ${benefits[0].cardBg} shadow-2xl shadow-[#8B5CF6]/10 border border-white/5 group relative overflow-hidden`}
                >
                  {/* Enhanced ambient glows */}
                  <motion.div
                    className="absolute top-0 right-0 w-[60%] h-[60%] rounded-full bg-[#7393B3]/15 blur-[80px] pointer-events-none"
                    animate={{ opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 6, repeat: Infinity }}
                  />
                  <motion.div
                    className="absolute bottom-0 left-0 w-[40%] h-[40%] rounded-full bg-[#8B5CF6]/12 blur-[60px] pointer-events-none"
                    animate={{ opacity: [0.08, 0.16, 0.08] }}
                    transition={{ duration: 8, repeat: Infinity, delay: 2 }}
                  />

                  <div className="relative z-10">
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefits[0].iconBg} flex items-center justify-center mb-8 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon>
                        <StarOutline />
                      </Icon>
                    </div>
                    <Typography
                      variant="h4"
                      className={`font-bold ${benefits[0].textColor} tracking-tight mb-4 text-xl md:text-2xl`}
                    >
                      {benefits[0].title}
                    </Typography>
                    <Typography
                      className={`${benefits[0].descColor} leading-relaxed text-[15px] max-w-lg`}
                    >
                      {benefits[0].desc}
                    </Typography>

                    {/* Animated brainwave decoration – more vivid */}
                    <svg
                      viewBox="0 0 400 40"
                      className="mt-8 w-full max-w-sm opacity-30"
                      preserveAspectRatio="none"
                    >
                      <motion.path
                        d="M0,20 Q30,20 60,20 T90,12 T120,28 T150,8 T180,32 T210,20 T240,15 T270,25 T300,10 T330,30 T360,20 T400,20"
                        fill="none"
                        stroke="url(#benefitWave)"
                        strokeWidth="2"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 2, delay: 0.5 }}
                      />
                      <defs>
                        <linearGradient id="benefitWave" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#7393B3" />
                          <stop offset="50%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#C084FC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </motion.div>
            </Grid>

            {/* Right column – two stacked frosted glass cards */}
            <Grid item xs={12} md={5}>
              <div className="flex flex-col gap-4 h-full">
                {benefits.slice(1).map((benefit, idx) => (
                  <motion.div
                    key={idx + 1}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, delay: (idx + 1) * 0.15 }}
                    className="flex-1"
                  >
                    <div
                      className="h-full p-8 rounded-[2rem] group relative overflow-hidden transition-all duration-500 hover:-translate-y-1 backdrop-blur-xl"
                      style={{
                        background: "rgba(255,255,255,0.45)",
                        border: "1px solid rgba(139,92,246,0.08)",
                        boxShadow: "0 4px 20px rgba(139,92,246,0.04), 0 0 0 1px rgba(255,255,255,0.5) inset",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = `0 16px 50px ${benefit.accentColor}15, 0 0 0 1px rgba(255,255,255,0.6) inset`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(139,92,246,0.04), 0 0 0 1px rgba(255,255,255,0.5) inset";
                      }}
                    >
                      {/* Decorative circle */}
                      <div
                        className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-[0.08] pointer-events-none"
                        style={{ background: benefit.accentColor }}
                      />

                      <div className="relative z-10">
                        <div
                          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.iconBg} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                          style={{ boxShadow: `0 6px 20px ${benefit.accentColor}25` }}
                        >
                          <benefit.Icon
                            sx={{ fontSize: 28, color: benefit.iconColor }}
                          />
                        </div>
                        <Typography
                          variant="h5"
                          className={`font-bold ${benefit.textColor} tracking-tight mb-3 text-lg`}
                        >
                          {benefit.title}
                        </Typography>
                        <Typography
                          className={`${benefit.descColor} leading-relaxed text-sm`}
                        >
                          {benefit.desc}
                        </Typography>
                      </div>

                      {/* Glowing accent line on hover */}
                      <div
                        className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{
                          background: `linear-gradient(90deg, ${benefit.accentColor}, transparent)`,
                          boxShadow: `0 0 20px ${benefit.accentColor}40`,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </Grid>
          </Grid>
        </div>
      </Container>
    </Box>
  );
};

export default BenefitsSection;
